import React, { useState, useEffect } from 'react';

// Fallback Icons
import iconFlight from '../../assets/images/fi_4586976.png';
import iconStay from '../../assets/images/Groupnesec.png';
import iconTour from '../../assets/images/Client_rating_x2C__testimonial_x2C__customers_x2C__star_team_x2C__team_x2C__group.png';
import iconTransport from '../../assets/images/fi_11835224.png';

// Fallback Carousel Image
import imgTajMahal from '../../assets/images/pexels-pulkid-mewada-3237758-35638858 1.png';

interface UnforgettableTripProps {
  unforgettableData?: {
    title?: string;
    description?: string;
    items?: Array<{
      title: string;
      description: string;
      image_url?: string;
    }>;
  };
  carouselData?: {
    title?: string;
    cta?: {
      text: string;
      link: string;
    };
    images?: Array<{
      image_url: string;
    }>;
  };
}

const UnforgettableTrip: React.FC<UnforgettableTripProps> = ({ unforgettableData, carouselData }) => {
  const [activeSlide, setActiveSlide] = useState(0);

  // Fallback features list
  const fallbackFeatures = [
    {
      icon: iconFlight,
      title: 'Flight Bookings',
      desc: 'Inclusive of both domestic and international ticketing.'
    },
    {
      icon: iconStay,
      title: 'Premium Stays',
      desc: 'Quality accommodations curated for families, groups, or solo travelers.'
    },
    {
      icon: iconTour,
      title: 'Expert Guided Tours',
      desc: 'Structured itineraries to explore regional heritage and landscapes.'
    },
    {
      icon: iconTransport,
      title: 'Comfortable and Safe Transportation.',
      desc: 'Added to ensure a completely hassle-free transition from arrival to stay.'
    }
  ];

  // Resolve unforgettable trip items
  const displayFeatures = unforgettableData?.items && unforgettableData.items.length > 0
    ? unforgettableData.items.map((item, idx) => ({
        icon: item.image_url || [iconFlight, iconStay, iconTour, iconTransport][idx % 4],
        title: item.title,
        desc: item.description
      }))
    : fallbackFeatures;

  // Resolve Carousel images list
  const carouselImages = carouselData?.images && carouselData.images.length > 0
    ? carouselData.images.map(img => img.image_url)
    : [imgTajMahal];

  // Auto-play timer for the premium slider
  useEffect(() => {
    if (carouselImages.length <= 1) return;

    const timer = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % carouselImages.length);
    }, 4500);

    return () => clearInterval(timer);
  }, [carouselImages.length]);

  // CTA navigation handler to bypass router errors
  const handleCtaClick = (link: string) => {
    if (link.startsWith('http://') || link.startsWith('https://')) {
      window.location.href = link;
    } else {
      window.location.href = '/contact';
    }
  };

  return (
    <section className="py-10 md:py-16 bg-white font-manrope">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">

        {/* Top Header Row */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          {unforgettableData?.title ? (
            <div 
              dangerouslySetInnerHTML={{ __html: unforgettableData.title }} 
              className="flex-grow max-w-2xl"
            />
          ) : (
            <h2 className="text-[#0a1e3f] text-3xl md:text-4xl lg:text-[42px] leading-[1.2] tracking-tight">
              <span
                className="text-[#c1272d] block mb-2"
                style={{ fontFamily: "'Renfrew', sans-serif" }}
              >
                Nisa Travel Agency
              </span>
              for next unforgettable trip
            </h2>
          )}
          <p className="text-[#5e6d7c] text-[15px] leading-relaxed max-w-sm">
            {unforgettableData?.description || "We offer tailored travel experiences perfect for families, groups, or solo travelers seeking stress-free vacations."}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mt-20">
          {displayFeatures.map((feature, idx) => (
            <div key={idx} className="flex flex-col items-center text-center group">
              <div className="w-[72px] h-[72px] mb-6 flex items-center justify-center p-2 rounded-2xl bg-[#F1F4F8] group-hover:bg-[#EAF0F6] transition-all duration-300 transform group-hover:scale-105">
                <img
                  src={feature.icon}
                  alt={feature.title}
                  className="w-full h-full object-contain"
                />
              </div>
              <h4 className="text-[#0a1e3f] text-[17px] font-extrabold mb-3 group-hover:text-[#c1272d] transition-colors duration-300">
                {feature.title}
              </h4>
              <p className="text-[#5e6d7c] text-[14px] leading-relaxed">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Huge Carousel Image Block */}
        <div className="relative mt-20 w-full h-[400px] md:h-[550px] lg:h-[600px] rounded-[24px] overflow-hidden shadow-2xl group">
          {/* Background Images Layer with Premium Cross-fade Animation */}
          {carouselImages.map((imgUrl, idx) => (
            <div 
              key={idx}
              className={`absolute inset-0 w-full h-full transition-all duration-1000 ease-in-out transform ${
                idx === activeSlide ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-105 z-0 pointer-events-none'
              }`}
            >
              <img
                src={imgUrl}
                alt="Discover the Heritage and Landscapes of India"
                className="w-full h-full object-cover"
              />
            </div>
          ))}

          {/* User's Custom Gradient Overlay */}
          <div
            className="absolute inset-0 z-20"
            style={{ background: 'linear-gradient(258.43deg, rgba(0, 0, 0, 0) 37.34%, #0E313A 100%)' }}
          ></div>

          {/* Content inside Image */}
          <div className="absolute bottom-12 md:bottom-20 left-6 md:left-16 z-30 max-w-xl">
            <h3 className="text-white text-3xl md:text-[40px] lg:text-[46px] font-bold leading-[1.15] tracking-tight mb-8 whitespace-pre-line">
              {carouselData?.title || "Discover the Heritage \n and Landscapes of India."}
            </h3>
            <button 
              onClick={() => handleCtaClick(carouselData?.cta?.link || '/contact')}
              className="bg-white hover:bg-gray-100 text-[#0a1e3f] font-extrabold text-[12px] md:text-[13px] tracking-wider uppercase px-8 py-4 rounded-[6px] transition-all active:scale-95 shadow-lg hover:shadow-xl"
            >
              {carouselData?.cta?.text || "BOOK YOUR TOUR"}
            </button>
          </div>

          {/* Carousel Dots indicator */}
          {carouselImages.length > 1 && (
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-30">
              {carouselImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveSlide(idx)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    idx === activeSlide 
                      ? 'bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)] scale-125' 
                      : 'bg-white opacity-40 hover:opacity-80'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>

      </div>
    </section>
  );
};

export default UnforgettableTrip;

